import React, { useEffect, useState, useRef, useCallback, useLayoutEffect, useMemo, ChangeEvent } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import { RootState, AppDispatch } from '../../store';
import { fetchTemplates } from '../../store/templatesSlice';
import { fetchAnswers, saveAnswer } from '../../store/answersSlice';
import { fetchPhotos, addPhoto } from '../../store/photosSlice';
import { fetchCover } from '../../store/coverSlice';
import { generatePdfForCurrentUser } from '../../services/useGeneratePDF';
import { TopNav } from '../../components/TopNav/TopNav';
import { Photo, Question, templatesApi } from '../../api';
import { calculatePagesFilled } from '../../utils/calculatePages';
import {
  BookPage,
  PAGE_PRESETS,
  DEFAULT_PAGE_PRESET,
  PagePreset,
} from '../../components/BookPage';
import { PhotoPage, decodePhotoLayout } from '../../components/PhotoPage';
import '../../styles/margin.css';
import './Questions.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

export const Questions: React.FC = () => {
  const { id: templateId } = useParams<{ id: string }>();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation('questions');
  const { t: tCommon } = useTranslation('common');
  const { t: tPhotos } = useTranslation('photos');

  const templates = useSelector((state: RootState) => state.templates.templates);
  const answers = useSelector((state: RootState) => state.answers.answers);
  const photos = useSelector((state: RootState) => state.photos.photos);
  const cover = useSelector((state: RootState) => state.cover.value);
  const loading = useSelector((state: RootState) => state.templates.loading);

  const template = templates.find(t => String(t.id) === templateId);
  const [questions, setQuestions] = useState<Question[]>([]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [text, setText] = useState('');
  const [clientQuestion, setClientQuestion] = useState('');
  const [hideQuestion, setHideQuestion] = useState(false);
  const [dropCap, setDropCap] = useState(false);
  // Буквицы кириллические — только для книг ru/kz (пустой язык = ru).
  const dropCapAvailable = !template?.language || template.language === 'ru' || template.language === 'kz';
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [drawer, setDrawer] = useState<null | 'chapters' | 'preview' | 'photos' | 'question'>(null);
  const [turningDirection, setTurningDirection] = useState<'next' | 'prev' | null>(null);
  const [pagePreset, setPagePreset] = useState<PagePreset>(DEFAULT_PAGE_PRESET);

  const handlePagePresetSelect = useCallback((id: PagePreset) => {
    setPagePreset(id);
    setHasChanges(true);
  }, []);


  const [pdfUrl, setPdfUrl] = useState<string>();
  const [numPages, setNumPages] = useState<number>();
  const [targetPage, setTargetPage] = useState<number>(1);

  // ============== add-photo popup ==============
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [photoModalUrl, setPhotoModalUrl] = useState<string>('');
  const [photoModalDesc, setPhotoModalDesc] = useState('');
  const [photoModalDate, setPhotoModalDate] = useState<string>('');
  const [photoModalUploading, setPhotoModalUploading] = useState(false);
  const [photoModalSaving, setPhotoModalSaving] = useState(false);
  const photoModalFileRef = useRef<HTMLInputElement>(null);

  const openPhotoModal = useCallback(() => {
    setPhotoModalUrl('');
    setPhotoModalDesc('');
    setPhotoModalDate('');
    setPhotoModalOpen(true);
  }, []);

  const closePhotoModal = useCallback(() => setPhotoModalOpen(false), []);

  const handlePhotoModalFile = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/heic', 'image/webp'];
    if (!allowed.includes(file.type)) {
      alert(tPhotos('alert_bad_format'));
      return;
    }
    setPhotoModalUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setPhotoModalUrl(response.data.url);
      if (photoModalFileRef.current) photoModalFileRef.current.value = '';
    } catch (err) {
      console.error('Photo upload failed:', err);
      alert(tPhotos('alert_upload_failed'));
    } finally {
      setPhotoModalUploading(false);
    }
  };

  const handlePhotoModalSave = async () => {
    if (!photoModalUrl || !templateId || !currentQuestion) return;
    setPhotoModalSaving(true);
    try {
      await dispatch(
        addPhoto({
          templateId: Number(templateId),
          url: photoModalUrl,
          description: photoModalDesc,
          date: photoModalDate || null,
          // Default fullbleed layout — popup keeps it minimal; fine-tune later via edit page.
          position: 'fullbleed::top:sm::bottom:md::light',
          questionTxt: currentQuestion.text,
        }),
      );
      closePhotoModal();
    } catch (err) {
      console.error('Failed to add photo:', err);
      alert(tPhotos('alert_upload_failed'));
    } finally {
      setPhotoModalSaving(false);
    }
  };
  const [rendered, setRendered] = useState<number>(0);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [pagesMap, setPagesMap] = useState<Record<string, number>>({});
  const [answersReady, setAnswersReady] = useState(false);

  // inline PDF preview — backend truth for current Q&A
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [previewNumPages, setPreviewNumPages] = useState<number>(0);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [pdfWidth, setPdfWidth] = useState<number>(520);
  const pdfWrapRef = useRef<HTMLDivElement>(null);

  const clientQuestionRef = useRef<HTMLTextAreaElement>(null);
  const answerRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const currentQuestion = questions[currentIndex];
  const currentAnswer = currentQuestion ? answers[currentQuestion.id] : null;

  const currentPhotos = useMemo(() => {
    if (!currentQuestion) return [];
    return Object.values(photos).filter(
      (photo: Photo) => photo.questionTxt === currentQuestion.text
    );
  }, [photos, currentQuestion]);

  // ============== initial fetches ==============

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/login', { replace: true });
      return;
    }
    dispatch(fetchTemplates());
  }, [dispatch, navigate]);

  useEffect(() => {
    if (templateId) {
      setAnswersReady(false);
      templatesApi.getQuestions(Number(templateId)).then(({ data }) => setQuestions(data));
      dispatch(fetchAnswers(Number(templateId))).then(() => {
        setAnswersReady(true);
      });
      dispatch(fetchPhotos(Number(templateId)));
      dispatch(fetchCover(Number(templateId)));
    }
  }, [templateId, dispatch]);

  useEffect(() => {
    if (!questions.length) return;
    const qParam = new URLSearchParams(location.search).get('q');
    if (!qParam) return;
    const targetIdx = questions.findIndex((q) => String(q.id) === qParam);
    if (targetIdx !== -1 && targetIdx !== currentIndex) {
      setCurrentIndex(targetIdx);
    }
  }, [questions, location.search]);

  useEffect(() => {
    setText(currentAnswer?.text || '');
    const savedClientQuestion = currentAnswer?.clientQuestion || '';
    setClientQuestion(savedClientQuestion === '-' ? '' : savedClientQuestion);
    setHideQuestion(savedClientQuestion === '-');
    setDropCap(!!currentAnswer?.dropCap);
    const savedPreset = currentAnswer?.preset;
    setPagePreset(
      PAGE_PRESETS.find((p) => p.id === savedPreset)?.id ?? DEFAULT_PAGE_PRESET,
    );
    setHasChanges(false);
  }, [
    currentAnswer?.text,
    currentAnswer?.preset,
    currentAnswer?.clientQuestion,
    currentAnswer?.dropCap,
    currentIndex,
  ]);

  useEffect(() => {
    if (clientQuestionRef.current) {
      clientQuestionRef.current.style.height = 'auto';
      clientQuestionRef.current.style.height = `${clientQuestionRef.current.scrollHeight}px`;
    }
    if (answerRef.current) {
      answerRef.current.style.height = 'auto';
      answerRef.current.style.height = `${answerRef.current.scrollHeight}px`;
    }
  }, [clientQuestion, hideQuestion, text]);

  // ============== PDF preview ==============

  const generatePdf = useCallback(async () => {
    setIsLoadingPdf(true);
    const res = await dispatch<any>(generatePdfForCurrentUser());
    if (!res?.blob) {
      setIsLoadingPdf(false);
      return;
    }
    const { blob, pagesMap: newPagesMap } = res as { blob: Blob; pagesMap: Record<string, number> };
    setRendered(0);
    setPagesMap(newPagesMap);
    setPdfUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(blob);
    });
    setIsLoadingPdf(false);
  }, [dispatch]);

  useEffect(() => {
    if (templateId && template && answersReady) {
      generatePdf();
    }
  }, [templateId, template?.id, answersReady, generatePdf]);

  // keep PDF page width matched to its container (responsive)
  useEffect(() => {
    const el = pdfWrapRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) setPdfWidth(Math.min(w, 520));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // fetch inline preview from backend: /api/pdf/:templateId/pages?q=<id>&preset=<preset>
  // refetches on question change, save, and preset change (live verstka switch).
  useEffect(() => {
    if (!templateId || !currentQuestion) return;
    let canceled = false;
    const fetchPreview = async () => {
      setIsPreviewLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${API_URL}/pdf/${templateId}/pages?q=${currentQuestion.id}&preset=${pagePreset}&dropCap=${dropCap ? 1 : 0}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!response.ok) throw new Error(`preview ${response.status}`);
        const blob = await response.blob();
        if (canceled) return;
        setPreviewPdfUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      } catch (err) {
        console.error('preview fetch failed', err);
      } finally {
        if (!canceled) setIsPreviewLoading(false);
      }
    };
    fetchPreview();
    return () => {
      canceled = true;
    };
  }, [templateId, currentQuestion?.id, currentAnswer?.updatedAt, pagePreset, dropCap]);

  const questionKey = currentQuestion?.text;

  useEffect(() => {
    if (!questionKey || !pagesMap) return;
    setTargetPage(pagesMap[questionKey] ?? 1);
  }, [questionKey, pagesMap]);

  const PAGE_WIDTH = 280;
  const PAGE_HEIGHT = PAGE_WIDTH * 1.5;
  const GAP = 24;

  useLayoutEffect(() => {
    if (!scrollRef.current) return;
    if (!numPages) return;
    if (rendered < numPages) return;
    scrollRef.current.scrollTo({
      top: (targetPage - 1) * (PAGE_HEIGHT + GAP),
      behavior: 'auto',
    });
  }, [rendered, numPages, targetPage]);

  const onPageRender = useCallback(() => {
    setRendered(n => n + 1);
  }, []);

  // ============== text handlers ==============

  const handleClientQuestionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setClientQuestion(e.target.value);
    setHasChanges(true);
  };

  const handleHideQuestionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setHideQuestion(isChecked);
    if (isChecked) {
      setClientQuestion('');
    } else {
      setClientQuestion(currentQuestion?.text || '');
    }
    setHasChanges(true);
  };

  // ============== navigation with page-turn ==============

  const goToIndex = useCallback((newIdx: number) => {
    if (newIdx === currentIndex) return;
    if (hasChanges) {
      if (!window.confirm(t('unsaved_confirm') as string)) {
        return;
      }
    }
    const direction: 'next' | 'prev' = newIdx > currentIndex ? 'next' : 'prev';
    setTurningDirection(direction);
    // mid-animation swap content
    window.setTimeout(() => {
      setCurrentIndex(newIdx);
      setDrawer(null);
      // прыгаем в начало страницы вместе со сменой контента — иначе новая
      // страница открывается со скролла предыдущей.
      window.scrollTo({ top: 0, behavior: 'auto' });
    }, 180);
    window.setTimeout(() => {
      setTurningDirection(null);
    }, 380);
  }, [currentIndex, hasChanges]);

  const handleQuestionSelect = (index: number) => {
    goToIndex(index);
  };

  const handlePrev = () => {
    if (currentIndex > 0) goToIndex(currentIndex - 1);
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) goToIndex(currentIndex + 1);
  };

  // ============== save + advance + chapter rite ==============

  const handleSave = async (andAdvance = false) => {
    if (!currentQuestion || !hasChanges) {
      if (andAdvance) handleNext();
      return;
    }
    setIsSaving(true);
    try {
      // Encode hide-question as the legacy '-' sentinel; an empty string means
      // "no rewrite, use the original question".
      const cq = hideQuestion
        ? '-'
        : clientQuestion && clientQuestion !== currentQuestion.text
          ? clientQuestion
          : '';
      await dispatch(saveAnswer({
        questionId: currentQuestion.id,
        text: text,
        preset: pagePreset,
        clientQuestion: cq,
        dropCap,
      }));
      setHasChanges(false);
      await generatePdf();

      if (andAdvance) {
        handleNext();
      }
    } finally {
      setIsSaving(false);
    }
  };

  // ============== misc ==============

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleBack = () => {
    navigate('/forms');
  };

  const answeredCount = Object.values(answers).filter(a =>
    a.text && a.text.trim().length > 0
  ).length;

  const photosArray = Object.values(photos);
  const pagesFilled = calculatePagesFilled(answers, photosArray.length);

  const isAnswered = (questionId: number) => {
    const ans = answers[questionId];
    return ans && ans.text && ans.text.trim().length > 0;
  };

  if (loading && !template) {
    return (
      <div className="mg-page">
        <TopNav onLogout={handleLogout} />
        <main className="mg-q__main"><div className="mg-q__loading">{t('loading')}</div></main>
      </div>
    );
  }

  if (!template || !currentQuestion) {
    return (
      <div className="mg-page">
        <TopNav onLogout={handleLogout} />
        <main className="mg-q__main"><div className="mg-q__loading">{t('template_not_found')}</div></main>
      </div>
    );
  }

  const stageClass = [
    'mg-q__stage',
    turningDirection === 'next' ? 'mg-q__stage--turn-next' : '',
    turningDirection === 'prev' ? 'mg-q__stage--turn-prev' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="mg-page mg-q">
      <TopNav
        onLogout={handleLogout}
        back={handleBack}
        title={t('nav_title')}
        actions={
          <span className="mg-q__topnav-actions">
            <button className="mg-q__nav-btn" onClick={() => setDrawer('chapters')} type="button">
              <svg
                className="mg-q__nav-btn-icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="18" x2="20" y2="18" />
              </svg>
              <span>{t('all_questions')}</span>
            </button>
            <button
              className="mg-btn mg-q__topline-save"
              onClick={() => handleSave(false)}
              disabled={isSaving || !hasChanges}
              type="button"
            >
              {isSaving ? t('saving') : hasChanges ? t('save') : t('saved')}
            </button>
          </span>
        }
      />

      {drawer && (
        <div
          className="mg-q__backdrop"
          onClick={() => setDrawer(null)}
        />
      )}

      <main className="mg-q__main">

        <div className="mg-q__inpage-actions">
          <button className="mg-q__nav-btn" onClick={() => setDrawer('chapters')} type="button">
            <svg
              className="mg-q__nav-btn-icon"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="18" x2="20" y2="18" />
            </svg>
            <span>{t('all_questions')}</span>
          </button>
          <button
            className="mg-btn mg-q__topline-save"
            onClick={() => handleSave(false)}
            disabled={isSaving || !hasChanges}
            type="button"
          >
            {isSaving ? t('saving') : hasChanges ? t('save') : t('saved')}
          </button>
        </div>

        <div className="mg-split">

          <div className="mg-q__page-wrap mg-split__preview">
            <div className="mg-q__pdf" ref={pdfWrapRef}>
              {isPreviewLoading && !previewPdfUrl && (
                <div className="mg-q__pdf-loading mg-meta">{t('preview_updating')}</div>
              )}
              {previewPdfUrl && (
                <Document
                  file={previewPdfUrl}
                  onLoadSuccess={({ numPages }) => setPreviewNumPages(numPages)}
                  loading={<div className="mg-q__pdf-loading mg-meta">{t('preview_loading')}</div>}
                  className="mg-q__pdf-doc"
                >
                  {Array.from({ length: previewNumPages }).map((_, i) => (
                    <Page
                      key={i}
                      pageNumber={i + 1}
                      width={pdfWidth}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                      className="mg-q__pdf-page"
                    />
                  ))}
                </Document>
              )}
            </div>
          </div>

          <section className={`${stageClass} mg-split__ui`}>
            <div className="mg-q__qhead">
              <div className="mg-label mg-q__qhead-label">
                {t('rite_question', { n: currentIndex + 1 })}
              </div>
              <button
                className="mg-q__tool"
                onClick={() => setDrawer('question')}
                type="button"
              >
                {t('edit_question')}
              </button>
            </div>

            <div className="mg-q__writer-prompt">
              {clientQuestion || currentQuestion.text}
            </div>

            <div className="mg-q__writer">
              <textarea
                ref={answerRef}
                className="mg-q__writer-input"
                value={text}
                onChange={(e) => { setText(e.target.value); setHasChanges(true); }}
                placeholder={t('writer_placeholder') as string}
                rows={1}
              />
            </div>

            <section className="mg-q__presets">
            {dropCapAvailable && (
            <label className="mg-checkbox mg-q__dropcap">
              <input
                type="checkbox"
                checked={dropCap}
                onChange={(e) => { setDropCap(e.target.checked); setHasChanges(true); }}
              />
              <span>{t('drop_cap')}</span>
            </label>
            )}
            <div className="mg-q__presets-head">
              <div className="mg-label">{t('presets_head')}</div>
            </div>
            <div className="mg-q__preset-grid">
              {PAGE_PRESETS.map((p) => {
                const isActive = p.id === pagePreset;
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`mg-q__preset-btn ${isActive ? 'mg-q__preset-btn--active' : ''}`}
                    onClick={() => handlePagePresetSelect(p.id)}
                  >
                    <BookPage
                      className={`mg-q__preset-thumb bp--flat`}
                      preset={p.id}
                      question={t('preset_sample_question') as string}
                      answer={t('preset_sample_answer') as string}
                      onChange={() => {}}
                      footerTitle={cover?.bookTitle || (t('preset_sample_title') as string)}
                      pageNumber={14}
                      readOnly
                    />
                    <div className="mg-q__preset-text">
                      <div className="mg-q__preset-name">{t(`page_preset_${p.id}_name`, { defaultValue: p.name })}</div>
                      <div className="mg-q__preset-desc">{t(`page_preset_${p.id}_desc`, { defaultValue: p.description })}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          <nav className="mg-q__pager" aria-label={t('pager_label') as string}>
            <button
              type="button"
              className="mg-q__pager-arrow"
              onClick={handlePrev}
              disabled={currentIndex === 0 || !!turningDirection}
              aria-label={t('prev_page') as string}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M15 6l-6 6 6 6" />
              </svg>
            </button>

            {(() => {
              const total = questions.length;
              const cur = currentIndex + 1;
              const w = 2;
              const lo = Math.max(1, cur - w);
              const hi = Math.min(total, cur + w);
              const items: (number | 'gap')[] = [];
              if (lo > 1) {
                items.push(1);
                if (lo > 2) items.push('gap');
              }
              for (let i = lo; i <= hi; i += 1) items.push(i);
              if (hi < total) {
                if (hi < total - 1) items.push('gap');
                items.push(total);
              }
              return items.map((it, i) =>
                it === 'gap' ? (
                  <span key={`gap-${i}`} className="mg-q__pager-gap" aria-hidden>…</span>
                ) : (
                  <button
                    key={it}
                    type="button"
                    className={`mg-q__pager-num ${it === cur ? 'mg-q__pager-num--active' : ''} ${isAnswered(questions[it - 1].id) ? 'mg-q__pager-num--done' : ''}`}
                    onClick={() => goToIndex(it - 1)}
                    disabled={!!turningDirection}
                    aria-current={it === cur ? 'page' : undefined}
                  >
                    {it}
                  </button>
                ),
              );
            })()}

            <button
              type="button"
              className="mg-q__pager-arrow"
              onClick={handleNext}
              disabled={currentIndex === questions.length - 1 || !!turningDirection}
              aria-label={t('next_page') as string}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
          </nav>

          <section className="mg-q__photos-section">
            <div className="mg-label mg-q__photos-section-head">{t('photos_for_question')}</div>
            <button
              className="mg-q__photo-attach mg-q__photo-attach--full"
              onClick={openPhotoModal}
              type="button"
            >
              <svg
                className="mg-q__photo-attach-icon"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <circle cx="8.5" cy="10" r="1.5" />
                <path d="M21 15l-5-5L5 19" />
              </svg>
              <span>{t('add_photo')}</span>
            </button>
            {currentPhotos.length > 0 && (
              <div className="mg-q__photos-cells">
                {currentPhotos.map((photo: Photo, idx: number) => {
                  const layout = decodePhotoLayout(photo.position);
                  // Только дата, проставленная юзером (photo.date); createdAt
                  // (таймстемп загрузки) в превью не подставляем — нет даты,
                  // нет подписи. Тот же подход, что в сетке на дашборде.
                  const iso = photo.date;
                  const d = iso ? new Date(iso) : null;
                  const dateStr =
                    d && !isNaN(d.getTime())
                      ? `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`
                      : '';
                  return (
                    <button
                      key={photo.id}
                      className="mg-q__photo-cell"
                      onClick={() =>
                        navigate(`/forms/photos/${templateId}/${photo.id}`)
                      }
                      type="button"
                    >
                      <div className="mg-q__photo-cell-stack">
                        <PhotoPage
                          className="mg-q__photo-cell-thumb"
                          preset={layout.preset}
                          photoUrl={photo.url || ''}
                          date={dateStr}
                          datePos={photo.showDate !== false ? layout.date.pos : 'hidden'}
                          dateSize={layout.date.size}
                          description={photo.description || ''}
                          descPos={photo.showDescription !== false ? layout.desc.pos : 'hidden'}
                          descSize={layout.desc.size}
                          textColor={layout.color}
                          footerTitle={cover?.bookTitle || ''}
                          pageNumber={idx + 1}
                        />
                      </div>
                      <div className="mg-q__photo-cell-meta">
                        <span className="mg-q__photo-cell-type">фото</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
          </section>
        </div>


      </main>

      {drawer === 'chapters' && (
        <aside className="mg-q__drawer mg-q__drawer--open">
          <div className="mg-q__drawer-head">
            <div className="mg-label">{t('drawer_questions')}</div>
            <button className="mg-q__drawer-close" onClick={() => setDrawer(null)} type="button">
              {t('drawer_close')}
            </button>
          </div>
          <div className="mg-q__qlist">
            <ol className="mg-q__qgroup-list">
              {questions.map((q, idx) => (
                <li
                  key={q.id}
                  className={`mg-q__qitem ${idx === currentIndex ? 'mg-q__qitem--active' : ''} ${isAnswered(q.id) ? 'mg-q__qitem--done' : ''}`}
                  onClick={() => handleQuestionSelect(idx)}
                >
                  <span className="mg-q__qitem-num">{String(idx + 1).padStart(2, '0')}</span>
                  <span className="mg-q__qitem-text">{q.text}</span>
                </li>
              ))}
            </ol>
          </div>
        </aside>
      )}

      {drawer === 'preview' && (
        <aside className="mg-q__drawer mg-q__drawer--wide mg-q__drawer--open">
          <div className="mg-q__drawer-head">
            <div className="mg-label">{t('drawer_preview')}</div>
            <button className="mg-q__drawer-close" onClick={() => setDrawer(null)} type="button">
              {t('drawer_close')}
            </button>
          </div>
          <div
            className={`mg-q__preview-box ${isLoadingPdf ? 'mg-q__preview-box--loading' : ''}`}
            ref={scrollRef}
          >
            {!pdfUrl ? (
              <div className="mg-q__preview-loader" />
            ) : (
              <Document
                file={pdfUrl}
                loading={<div className="mg-q__preview-loader" />}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              >
                {Array.from({ length: numPages ?? 0 }, (_, i) => (
                  <Page
                    key={i}
                    pageNumber={i + 1}
                    width={PAGE_WIDTH}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    onRenderSuccess={onPageRender}
                  />
                ))}
              </Document>
            )}
          </div>
        </aside>
      )}

      {drawer === 'question' && (
        <aside className="mg-q__drawer mg-q__drawer--open">
          <div className="mg-q__drawer-head">
            <div className="mg-label">{t('drawer_question')}</div>
            <button className="mg-q__drawer-close" onClick={() => setDrawer(null)} type="button">
              {t('drawer_close')}
            </button>
          </div>
          <div className="mg-q__drawer-body">
            <p className="mg-q__drawer-note mg-meta">{t('question_edit_hint')}</p>

            <label className="mg-checkbox">
              <input
                type="checkbox"
                checked={hideQuestion}
                onChange={handleHideQuestionChange}
              />
              <span>{t('hide_question_in_book')}</span>
            </label>

            {!hideQuestion && (
              <div className="mg-field" style={{ marginTop: 24 }}>
                <label className="mg-field__label">{t('question_text_label')}</label>
                <textarea
                  ref={clientQuestionRef}
                  className="mg-textarea"
                  value={clientQuestion || currentQuestion.text}
                  onChange={handleClientQuestionChange}
                  placeholder={t('rewrite_placeholder') as string}
                  rows={2}
                  maxLength={150}
                />
              </div>
            )}
          </div>
        </aside>
      )}

      {photoModalOpen && (
        <div className="mg-popup-overlay" onClick={closePhotoModal}>
          <div className="mg-popup mg-q__photo-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mg-popup__title">{t('add_photo')}</div>

            <div className="mg-q__photo-modal-stage">
              {photoModalUrl ? (
                <img src={photoModalUrl} alt="" className="mg-q__photo-modal-img" />
              ) : (
                <button
                  type="button"
                  className="mg-q__photo-modal-drop"
                  onClick={() => photoModalFileRef.current?.click()}
                  disabled={photoModalUploading}
                >
                  {photoModalUploading ? tPhotos('uploading') : tPhotos('choose_file')}
                </button>
              )}
              <input
                ref={photoModalFileRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/heic,image/webp"
                style={{ display: 'none' }}
                onChange={handlePhotoModalFile}
              />
              {photoModalUrl && (
                <button
                  type="button"
                  className="mg-btn--text"
                  onClick={() => photoModalFileRef.current?.click()}
                  disabled={photoModalUploading}
                >
                  {photoModalUploading ? tPhotos('uploading') : tPhotos('choose_another')}
                </button>
              )}
            </div>

            <div className="mg-field">
              <label className="mg-field__label">{tPhotos('field_description')}</label>
              <input
                type="text"
                className="mg-input"
                value={photoModalDesc}
                onChange={(e) => setPhotoModalDesc(e.target.value)}
                maxLength={200}
                placeholder={tPhotos('description_placeholder') as string}
              />
            </div>

            <div className="mg-field">
              <label className="mg-field__label">{tPhotos('field_date')}</label>
              <input
                type="date"
                className="mg-input"
                value={photoModalDate}
                onChange={(e) => setPhotoModalDate(e.target.value)}
              />
            </div>

            <div className="mg-popup__actions">
              <button
                className="mg-btn"
                onClick={handlePhotoModalSave}
                disabled={!photoModalUrl || photoModalSaving}
                type="button"
              >
                {photoModalSaving ? tPhotos('saving') : tPhotos('attach_to_book')}
              </button>
              <button
                className="mg-btn--text-muted"
                onClick={closePhotoModal}
                disabled={photoModalSaving}
                type="button"
              >
                {tCommon('cancel') as string}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
